import { disableGtm, enableGtm } from "@/lib/gtm";

const GA4_SCRIPT_ID = "eira-ga4-gtag-script";
const META_PIXEL_SCRIPT_ID = "eira-meta-pixel-script";
export const TRACKING_CONSENT_KEY = "eira-tracking-consent";

let activeMeasurementId = "";
let activeMetaPixelId = "";
let trackingSettings = { measurementId: "", metaPixelId: "" };
let lastTrackedMetaPageUrl = "";

const ensureGtagQueue = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
};

const disableKey = (measurementId) => `ga-disable-${measurementId}`;

const consentEventName = "eira-tracking-consent-change";

const normalizeMetaPixelId = (pixelId) => String(pixelId || "").trim();

const browserStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const getTrackingConsent = () => {
  const choice = browserStorage()?.getItem(TRACKING_CONSENT_KEY);
  return choice === "accepted" || choice === "declined" ? choice : "unknown";
};

const announceConsentChange = () => {
  window.dispatchEvent(new Event(consentEventName));
};

export const enableAnalytics = (measurementId) => {
  const normalizedId = String(measurementId || "").trim().toUpperCase();
  if (!normalizedId) {
    disableAnalytics();
    return;
  }
  if (activeMeasurementId === normalizedId && document.getElementById(GA4_SCRIPT_ID)) {
    return;
  }
  disableAnalytics();
  activeMeasurementId = normalizedId;
  window[disableKey(normalizedId)] = false;
  ensureGtagQueue();
  window.gtag("js", new Date());
  window.gtag("config", normalizedId);
  const script = document.createElement("script");
  script.id = GA4_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(normalizedId)}`;
  script.dataset.measurementId = normalizedId;
  document.head.appendChild(script);
};

export const disableAnalytics = () => {
  const script = document.getElementById(GA4_SCRIPT_ID);
  const measurementId = activeMeasurementId || script?.dataset.measurementId;
  if (measurementId) {
    window[disableKey(measurementId)] = true;
  }
  script?.remove();
  activeMeasurementId = "";
  delete window.gtag;
};

export const analyticsMeasurementId = () => activeMeasurementId;

export const disableMetaPixel = () => {
  document.getElementById(META_PIXEL_SCRIPT_ID)?.remove();
  activeMetaPixelId = "";
  lastTrackedMetaPageUrl = "";
  delete window.fbq;
  delete window._fbq;
};

export const enableMetaPixel = (pixelId) => {
  const normalizedId = normalizeMetaPixelId(pixelId);
  if (!normalizedId) {
    disableMetaPixel();
    return;
  }
  if (activeMetaPixelId === normalizedId && document.getElementById(META_PIXEL_SCRIPT_ID)) {
    return;
  }
  disableMetaPixel();
  const fbq = function fbq() {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, arguments);
      return;
    }
    fbq.queue.push(arguments);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  fbq.disablePushState = true;
  window.fbq = fbq;
  window._fbq = fbq;
  const script = document.createElement("script");
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  script.dataset.pixelId = normalizedId;
  document.head.appendChild(script);
  fbq("init", normalizedId);
  activeMetaPixelId = normalizedId;
};

export const setTrackingSettings = ({ measurementId, metaPixelId }) => {
  trackingSettings = {
    measurementId: String(measurementId || "").trim().toUpperCase(),
    metaPixelId: normalizeMetaPixelId(metaPixelId),
  };
};

export const applyTrackingSettings = (settings) => {
  setTrackingSettings({
    measurementId: settings.measurement_id ?? settings.google_analytics_measurement_id,
    metaPixelId: settings.meta_pixel_id,
  });
};

export const activateAcceptedTracking = () => {
  if (getTrackingConsent() !== "accepted") {
    disableAnalytics();
    disableMetaPixel();
    disableGtm();
    return;
  }
  if (trackingSettings.measurementId) {
    enableAnalytics(trackingSettings.measurementId);
  } else {
    disableAnalytics();
  }
  if (trackingSettings.metaPixelId) {
    enableMetaPixel(trackingSettings.metaPixelId);
  } else {
    disableMetaPixel();
  }
  enableGtm();
};

export const acceptTracking = () => {
  browserStorage()?.setItem(TRACKING_CONSENT_KEY, "accepted");
  activateAcceptedTracking();
  announceConsentChange();
};

export const declineTracking = () => {
  browserStorage()?.setItem(TRACKING_CONSENT_KEY, "declined");
  disableAnalytics();
  disableMetaPixel();
  disableGtm();
  announceConsentChange();
};

export const trackingConsentEvent = () => consentEventName;

export const trackMetaPageView = (url = window.location.href) => {
  if (getTrackingConsent() !== "accepted" || !activeMetaPixelId || !window.fbq) {
    return;
  }
  if (lastTrackedMetaPageUrl === url) {
    return;
  }
  lastTrackedMetaPageUrl = url;
  window.fbq("track", "PageView");
};

export const trackMetaAddToCart = ({ id, name, value, quantity = 1 }) => {
  if (getTrackingConsent() !== "accepted" || !activeMetaPixelId || !window.fbq) {
    return;
  }
  const unitPrice = Number(value);
  const itemQuantity = Number(quantity) || 1;
  const amount = unitPrice * itemQuantity;
  if (!id || !Number.isFinite(amount) || amount < 0) {
    return;
  }
  window.fbq("track", "AddToCart", {
    content_ids: [String(id)],
    content_name: String(name || "Product"),
    content_type: "product",
    contents: [{ id: String(id), quantity: itemQuantity }],
    currency: "INR",
    value: amount,
  });
};

export const trackMetaPurchase = (order) => {
  if (getTrackingConsent() !== "accepted" || !activeMetaPixelId || !window.fbq) {
    return;
  }
  const orderId = String(order?.id || "");
  const amount = Number(order?.totals?.total);
  if (!orderId || !Number.isFinite(amount) || amount < 0) {
    return;
  }
  const sessionKey = `eira-meta-purchase:${orderId}`;
  const storage = (() => {
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  })();
  if (storage?.getItem(sessionKey)) {
    return;
  }
  const items = Array.isArray(order?.items) ? order.items : [];
  window.fbq("track", "Purchase", {
    content_ids: items.map((item) => String(item.product_id || item.id || item.name)),
    content_type: "product",
    currency: "INR",
    value: amount,
  });
  storage?.setItem(sessionKey, "1");
};